import { Fragment, ReactNode } from 'react';
import cx from 'classnames';
import Text from 'components/Text';
import { Colour } from 'components/Text/types';

import styles from './dashboard-card.module.scss';
import { Tooltip } from 'react-tooltip';

type TDashboardCard = {
  title: ReactNode;
  subtitle?: string;
  children?: ReactNode;
  badge?: ReactNode;
  headerAction?: ReactNode;
  glow?: boolean;
  className?: string;
};

const getColour = (colourThresholds: TGradientGrid['colourThresholds'], value: number) =>
  colourThresholds.find((threshold) => value >= threshold.min && value <= (threshold.max || value));

export default function DashboardCard({
  title,
  subtitle,
  children,
  badge,
  headerAction,
  glow,
  className
}: TDashboardCard) {
  return (
    <div className={cx(styles['dashboard-card'], { [styles['dashboard-card--glow']]: glow }, className)}>
      {glow && <div className={styles['dashboard-card__glow-ring']} />}
      <div className={styles['dashboard-card__header']}>
        <div className={styles['dashboard-card__heading']}>
          <Text type="heading2" className={styles['dashboard-card__title']}>
            {title}
          </Text>
          {subtitle && (
            <Text type="label" className={styles['dashboard-card__subtitle']}>
              {subtitle}
            </Text>
          )}
        </div>
        {headerAction && <div className={styles['dashboard-card__header-action']}>{headerAction}</div>}
      </div>

      <div className={styles['dashboard-card__content']}>{children}</div>
      {badge && <div className={styles['dashboard-card__badge']}>{badge}</div>}
    </div>
  );
}

const UNIT_ABBREVIATIONS: Record<string, string> = {
  hours: 'hrs',
  hour: 'hr',
  minutes: 'mins',
  minute: 'min',
  seconds: 'secs',
  second: 'sec',
  days: 'days',
  day: 'day',
  months: 'mths',
  month: 'mth'
};

function WithText({
  text,
  colourThresholds,
  value,
  badge,
  ...props
}: { value?: number; text: string; colourThresholds?: TGradientGrid['colourThresholds'] } & Omit<
  TDashboardCard,
  'children'
>) {
  const match = text.match(/^([\d.,]+)\s*(.*)$/);
  const numeral = match?.[1];
  const rawUnit = match?.[2];
  const unit = rawUnit ? (UNIT_ABBREVIATIONS[rawUnit.toLowerCase()] ?? rawUnit) : undefined;
  const thresholdColour = colourThresholds
    ? getColour(colourThresholds, value ?? parseFloat(text))?.background
    : undefined;

  return (
    <DashboardCard {...props}>
      <div className={styles['dashboard-card--with-text__stack']}>
        <div className={styles['dashboard-card--with-text__value']}>
          <Text
            color={thresholdColour as Colour | undefined}
            type="heading1"
            className={cx(styles['dashboard-card--with-text__text'], 'font-display', {
              [styles['dashboard-card--with-text__text--compact']]: !numeral
            })}
          >
            {numeral ?? text}
          </Text>
          {unit && <span className={styles['dashboard-card--with-text__unit']}>{unit}</span>}
        </div>
        {badge}
      </div>
    </DashboardCard>
  );
}

type TGradientGrid = {
  data: { name: string; value: number }[];
  colourThresholds: { min: number; max?: number; background?: string; stroke?: string }[];
};

function GradientGrid({ data, colourThresholds, ...props }: TGradientGrid & TDashboardCard) {
  const getStylesFromColour = (threshold?: TGradientGrid['colourThresholds'][0]) => {
    return threshold
      ? { background: threshold.background, border: threshold.stroke ? `1px solid ${threshold.stroke}` : void 0 }
      : {};
  };

  return (
    <DashboardCard {...props}>
      <div className={styles['dashboard-card--with-gradient-grid']}>
        {data.map(({ name, value }, index) => (
          <Fragment key={`${name}-${index}`}>
            <Tooltip id={name} delayShow={500} />
            <div
              data-tooltip-id={name}
              data-tooltip-content={`${name}: ${value}`}
              className={styles['dashboard-card--with-gradient-grid__item']}
              style={{ ...getStylesFromColour(getColour(colourThresholds, value)) }}
            />
          </Fragment>
        ))}
      </div>
    </DashboardCard>
  );
}

DashboardCard.Text = WithText;
DashboardCard.GradientGrid = GradientGrid;
